import Vue from 'vue';
import KEYS from '../../../values/keys';
import {
  SECTIONS_TYPE_FILE_DOWLOAD,
  SECTION_TYPES,
  TYPE_BOARD_DRAFT_FEEDBACK_OPTIONS,
  TYPE_BOARD_DRAFT_RESPONSE_OPTIONS
} from '../../../content/values/sections';
import  EdHomeworkViewer  from '../ed-homework-viewer/ed-homework-viewer.vue';
import { SectionsProvider } from '@/content/providers/sections.provider';
import {
  HOMEWORK_STATUS,
  _HOMEWORKS_STORE,
  HOMEWORKS_EVENTS,
  TEACHER_HOMEWORK_STATUS_ICON,
  HOMEWORK_HISTORY_STATUS
} from '@/homework/values/homeworks.values';
import { WriteObjectMixin } from '@/courses/mixins/write-object.mixin';
import { HomeworkMixin } from '@/homework/mixins/homework.mixin';
import ToolService from '@/providers/tools';
import { SERVER_HOSTNAME } from '@/shared/enums/request.enum';
import { AuthenticatedFileDownloadProvider } from '@/shared/providers/authenticated-file-download.provider';
import EdListHomeworkHistoryComponent from '@/homework/component/ed-list-homework-history/ed-list-homework-history.component.vue';

export default Vue.component('ed-homework-student-list-item', {
  name: 'ed-homework-student-list-item',
  props: ['homework'],
  mixins: [HomeworkMixin, WriteObjectMixin],
  components: {
    EdHomeworkViewer,
    EdListHomeworkHistoryComponent
  },
  data: () => {
    return {
      viewerResponse: EdHomeworkViewer,
      history: null
    };
  },
  computed: {
    isHomeworkGraded() {
      return (this.homework.status == HOMEWORK_STATUS.GRADED || this.homework.status == HOMEWORK_STATUS.FINISHED);
    },
    isHomeworkDraft() {
      return this.homework.status === HOMEWORK_STATUS.DRAFT || !this.homework.status;
    },
    isHomeworkFeedbackDraft() {
      return this.homework.status === HOMEWORK_STATUS.FEEDBACK_DRAFT;
    },
    isHomeworkSeen() {
      return this.homework.status === HOMEWORK_STATUS.SEEN;
    },
    urlDownload() {
      if(SECTIONS_TYPE_FILE_DOWLOAD.some(fileType => fileType === this.homework.type) ||
          this.isLessonTextRespondedWithPdf(this.homework)) {
        const asset = this.homework.type === SECTION_TYPES.LESSON_TEXT.code
            ? this.homework.section.assetsInfo[0].pdfHash
            : this.homework.text;
        let name = this.homework.name;
        let nameWithExt;
        if (SECTION_TYPES.LESSON_IMAGES.code === this.homework.type) {
          nameWithExt = this.safeBase64Encode(name + '.jpg');
          let nameReplaced = nameWithExt.replace(/\//g, '_');
          return `${process.env[SERVER_HOSTNAME.COMMON]}/download/object/image/${asset}/${nameReplaced}`;
        } else {
          let ext = SectionsProvider.getExtensionFromSectionType(this.homework.type);
          nameWithExt = this.safeBase64Encode(name + '.' + ext);
          let nameReplaced = nameWithExt.replace(/\//g, '_');
          return `${process.env[SERVER_HOSTNAME.COMMON]}/download/object/${ext}/${asset}/${nameReplaced}`;
        }
      }
      return null;
    },
    isHomeworkNew() {
      return this.homework.status === HOMEWORK_STATUS.NEW;
    },
    gradeTooltip() {
      return this.isHomeworkGraded ? this.$i18n.t('homeworks.alreadyGraded') : this.$i18n.t('homeworks.setGrade');
    },
    feedbackTooltip() {
      return this.isHomeworkGraded ? this.$i18n.t('homeworks.feedbackSent') : this.$i18n.t('homeworks.uploadFeedback');
    },
    canSendFeedback() {
      if(this.isLessonTextRespondedWithPdf(this.homework)) {
        return false;
      }
      return false;
    },
    statusIcon() {
      return TEACHER_HOMEWORK_STATUS_ICON[this.homework.status];
    },
    boardGroupHash() {
      return this.homework.boardGroup;
    },
    responseDraft() {
      return this.homework.responseDraft;
    },
    feedbackDraft() {
      return this.homework.feedbackDraf;
    },
    section() {
      return this.homework.section;
    },
    validateResponseDraftCurrentVersion(){
      return this.responseDraft && this.responseDraft.type === SECTION_TYPES.LESSON_BOARD.code;
    },  
    validateFeedbackDraftCurrentVersion(){
      return this.feedbackDraft && this.feedbackDraft.type === SECTION_TYPES.LESSON_BOARD.code;
    },
    homeworkEscapedName() {
      return ToolService.exitSpecialChars(this.homework.name);
    },
    person() {
      return this.$store.state.HomeworksStore.teacherWidgetHomeworks.personSelected;
    },
    canOpenWithCurrentVersion() : boolean {
      return ((this.validateDraftBoardOptions(this.section, TYPE_BOARD_DRAFT_RESPONSE_OPTIONS) && this.validateResponseDraftCurrentVersion)
              || (this.validateDraftBoardOptions(this.section, TYPE_BOARD_DRAFT_FEEDBACK_OPTIONS) && this.validateFeedbackDraftCurrentVersion))
          || (!this.responseDraft && !this.feedbackDraft);
    }

  },
  methods: {
    onClickFeedback(){
      if(!this.isHomeworkGraded) {
        this.$emit('onShowFeedback');
      }
    },
    async downloadHomework() {
      this.setSeenStatusDownloading();
      switch(this.homework.section.type) {
        case SECTION_TYPES.LESSON_SPLIT_WRITE.code:
        case SECTION_TYPES.LESSON_SPLIT_TYPE.code:
          await this.downloadFiles();
        break;
        default: {
          await AuthenticatedFileDownloadProvider.download(this.urlDownload, this.homework.name);
        }
      }
    },
    async viewHomework(clearHistory) {
      try {
        if(clearHistory){
          this.history = null;
        }
        if(this.homework.homeworkAssetInfo) {
          let response = await this.validateHomeworkStatus(this.homework.homeworkAssetInfo.hash);
          if(!response) {
            this.createMessageModal(String(this.$i18n.t('homeworks.documentStatus')), KEYS.EVENT_TYPES.WARNING);
            return;
          }
        }
        this.setSeenStatus();
        this.createModal();
      } catch(err) {
        throw new Error(err);
      }
    },
    createMessageModal(body , type) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('common.information'),
        body: body,
        type: type
      });
    },
    setSeenStatus() {
      let student = this.$store.state.HomeworksStore.teacherWidgetHomeworks.personSelected;
      if(this.isHomeworkDraft) {
        return;
      }
      if(this.homework.status !== HOMEWORK_STATUS.NEW){
        let homework = { ...this.homework, status: HOMEWORK_STATUS.DRAFT };
        homework.status = this.isHomeworkSeen && this.validateDraftBoardOptions(this.homework.section, TYPE_BOARD_DRAFT_FEEDBACK_OPTIONS)
                          ? HOMEWORK_STATUS.FEEDBACK_DRAFT
                          : this.homework.status;
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_TEACHER_HOMEWORK_STATUS, { student, homework });
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.REFRESH_PERSON_STATUS, {student, isReload: false});
        this.updateWidgetTeacherStatus(homework, student);
        return;
      }
 
      let homework = { ...this.homework, status: HOMEWORK_STATUS.SEEN };

      if(this.validateDraftBoardOptions(this.homework.section, TYPE_BOARD_DRAFT_FEEDBACK_OPTIONS)) {
        homework.status = HOMEWORK_STATUS.FEEDBACK_DRAFT;
      }
      
      this.updateWidgetTeacherStatus(homework, student);

      this.$store.dispatch(_HOMEWORKS_STORE.ACTIONS.UPDATE_TEACHER_HOMEWORK_STATUS, { 
        req: homework, student
      });
    },
    updateWidgetTeacherStatus(homework, student) {
      this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UDPATE_WIDGET_TEACHER_COURSE_SECTION_STATUS,
        {courseId: homework.courseId,
        classId: homework.classId,
        sectionId: homework.section.id,
        studentId: student.id,
        status: homework.status});
    },
    setSeenStatusDownloading() {
      if(this.homework.status !== HOMEWORK_STATUS.NEW){
        return;
      }

      let student = this.$store.state.HomeworksStore.teacherWidgetHomeworks.personSelected;
      let homework = { ...this.homework, status: HOMEWORK_STATUS.SEEN };
      this.$store.dispatch(_HOMEWORKS_STORE.ACTIONS.UPDATE_TEACHER_HOMEWORK_STATUS, { 
        req: homework, student
      });
    },
    createModal() {
      let student = this.$store.state.HomeworksStore.teacherWidgetHomeworks.personSelected;
      let feedbackDraft = this.homework.homeworkDraftAssetInfo;
      let params: any = {
        section : {
          id: this.homework.id,
          name: this.homework.name,
          assetsInfo: [this.homework.homeworkAssetInfo],
          type: this.homework.type,
          text: this.homework.text,
          description : this.homework.description,
          sections: this.homework.sections,
          section: this.homework.section,
          status: this.homework.status,
          homeworkDraftAssetInfo: feedbackDraft ? feedbackDraft : null,
          courseId: this.homework.courseId,
          classId: student.classId,
          sectionId: this.homework.section.id,
          version: this.homework.section.version
        },
        student,
        canResponse: false,
        history: this.history
        ,responseDraft: this.homework.responseDraft,
      };
      params.viewer = this.getHomeworkViewer(this.homework, 'viewerResponse');
      if(this.isHomeworkDraft) {
        this.showLoading()
        const title = this.$i18n.t('errors.ups');
        const body = this.$i18n.t('homeworks.homeworkDraftNotAvailable');
        if(this.homework.type === SECTION_TYPES.LESSON_MATERIAL.code) {
          // tell the viewer that show this as pdf
          params.section.type = SECTION_TYPES.LESSON_PDF.code;
        }
        this.getCurrentHomeworkDraft(this.homework.section.id, student.id)
            .then(res => {
              params.section.assetsInfo = [res.homeworkDraftAssetInfo];
              this.$EdModal.createModal(this.viewerResponse, params);
              this.hideLoading()
            })
            .catch(() => {
              this.$EdModal.createMessageModal({
                title: title,
                body: body,
                type: KEYS.EVENT_TYPES.DANGER
              });
              this.hideLoading()
            });
      } else {
        this.$EdModal.createModal(this.viewerResponse, params);
      }
    },
    updateStatus(homework) {
      if(homework.id === this.homework.id) {
        let student = this.$store.state.HomeworksStore.teacherWidgetHomeworks.personSelected;
        let homeworkUpdate = { ...this.homework, status: homework.status };
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_TEACHER_HOMEWORK_STATUS, { student, homework: homeworkUpdate });
      }
    },
    async downloadFiles() {
      let response = this.homework;
      let sections = this.homework.section.sections;
      let problem = null;
      let solution = null;
      let answer = null;
      if(this.homework.section.assetsInfo) {
        answer = {
          type: SECTION_TYPES.LESSON_PDF.code,
          assetsInfo: this.homework.section.assetsInfo,
          text: this.homework.section.text
        };
      }
      if(sections) {
        sections.forEach((s) => {
          problem  = this.getProblemToDownload(s, problem)
          solution = this.getSolutionToDownload(s, solution);
        });
      }
      let nameWithExt = this.safeBase64Encode(this.homework.name + '.zip');
      let nameReplaced = nameWithExt.replace(/\//g, '_');
      let files = `${nameReplaced}?hash=${problem.assetsInfo[0].hash}`;
      if(answer) {
        files += `&hash=${answer.assetsInfo[0].hash}`;
      }
      if(solution) {
        files += `&hash=${solution.assetsInfo[0].hash}`;
      }
      if(response && response.homeworkAssetInfo && response.homeworkAssetInfo.hash) {
        files += `&hash=${response.homeworkAssetInfo.hash}`;
      }
      await AuthenticatedFileDownloadProvider.download(
        `${process.env[SERVER_HOSTNAME.COMMON]}/download/object/zip/${files}`,
        `${this.homework.name}.zip`
      );
    },

    obtainDraftResponseParams(viewerKey : string = 'viewerResponse'): any {
      return {
        section: {
          ...this.responseDraft,
          assetsInfo: this.section.assetsInfo ? [...this.section.assetsInfo] : null,
          section: this.section,
          type: this.section.type,
          text: this.section.text,
          sectionId: this.section.id,
          classId: this.homework.classId,
          studentId: this.person.id,
          sections: this.section.sections
        },
        student: this.person,
        canResponse: false,
        viewer: this.getHomeworkViewer({...this.responseDraft, section: this.section}, viewerKey)
      };
    },

    joinHomework() {
      if (!this.canOpenWithCurrentVersion) {
        this.showCantOpenHomeworkModal();
        return;
      }

      if (this.isHomeworkDraft) {
        this.section.homeworkStatus = HOMEWORK_STATUS.DRAFT;
      } else {
        this.setSeenStatus();
      }

      let params = this.obtainDraftResponseParams('viewerHomework');
      this.$EdModal.createModal(EdHomeworkViewer, {...params, canResponse: true});
    },

    openHistory() {
      let student = this.$store.state.HomeworksStore.teacherWidgetHomeworks.personSelected;
      let data = {
        classId: this.course.classId,
        sectionId: String(this.section.id),
        sectionName: this.section.name,
        historyStatus: HOMEWORK_HISTORY_STATUS.FEEDBACK,
        studentId: student.id,
        title: this.section.name,
        homeworkId: this.homework.id,
      }
      this.$EdModal.createModal(EdListHomeworkHistoryComponent, {data});
    },

    openSnapShot(params) {
      if (String(params.sectionId) === String(this.section.id)){
        this.history = {...params, show:true};
        this.viewHomework();
      }
    },
    showLoading(): void {
      try {
        if (this.$refs.loading) this.$refs.loading['show']();
      } catch (e) {
          //
      }
    },  
    hideLoading(): void {
      if (this.$refs.loading) {
        this.$refs.loading['hide']();
      }
    },

    safeBase64Encode(input: string) {
      return btoa(String.fromCharCode(...new TextEncoder().encode(input)));
    },
  },
  created() {
    this.$edEventBus.$on(HOMEWORKS_EVENTS.UPDATE_HOMEWORK_STATUS, this.updateStatus);
    this.$edEventBus.$on(HOMEWORKS_EVENTS.OPEN_HOMEWORK_SNAPSHOT, this.openSnapShot);
  },
  beforeDestroy() {
    this.$edEventBus.$off(HOMEWORKS_EVENTS.OPEN_HOMEWORK_SNAPSHOT, this.openSnapShot);
  }

});
